import Cookies from 'js-cookie'
import PropsTypes from 'prop-types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Badge, Button, Col, Modal, Row, Spinner } from 'react-bootstrap'
import { SRLWrapper } from 'simple-react-lightbox'
import Swal from 'sweetalert2'
import config from '../../../../config'
import Colors from '../../../../constants/Colors'
import { bankCode } from '../../../../constants/constants'
import { KYC_STATUSES, KYC_STATUSES_COLOR_LIGHT, KYC_CARD_TYPE } from '../../constants'
import KYCBankForm from './KYCBankForm'
import KYCIdCardForm from './KYCIdCardForm'
import UserDetailCard from './UserDetailCard'

const requiredThai = [
  'firstName',
  'lastName',
  'cardId',
  'cardType',
  'birthDate',
  'address',
  'expiredAt',
  'issueAt',
  'neverExpire',
  'status',
]

const requiredForeign = ['firstName', 'lastName', 'cardId', 'cardType', 'birthDate', 'reasons']
const requiredBank = ['bankCode', 'accountNo', 'status', 'reasons']

export const KYCUserModal = ({ kycUserModalVisible, selectedKYCUser, onClose }) => {
  const userKYCDataRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [userKYCData, setUserKYCData] = useState(null)
  const [decisionIdCard, setDecisionIdCard] = useState('')
  const [reasonIdCard, setReasonIdCard] = useState([''])
  const [decisionBank, setDecisionBank] = useState('')
  const [reasonBank, setReasonBank] = useState([])
  const [userData, setUserData] = useState(null)
  const [invalid, setInvalid] = useState({
    reasonIdCard: false,
    reasonBank: false,
  })

  const fetchKYC = useCallback(async (userId) => {
    try {
      setLoading(true)
      const response = await fetch(`${config.apiAdminGateway}/api/v1/admin/kyc/${userId}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.description)

      const userKYCData = {
        ...response.data,
        cardInfo: {
          ...response.data.cardInfo,
          birthDate: {
            year: '',
            month: '',
            day: '',
          },
          issueAt: {
            year: '',
            month: '',
            day: '',
          },
          expiredAt: {
            year: '',
            month: '',
            day: '',
          },
          neverExpire: false,
        },
      }

      setUserKYCData(userKYCData)
      userKYCDataRef.current = userKYCData

      if (response.data.user.userId) {
        await fetchUserData(response.data.user.userId)
      }
    } catch (error) {
      setUserKYCData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchUserData = async (userId) => {
    try {
      const response = await fetch(`${config.apiAdminGateway}/api/v1/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.description)

      setUserData(response.data)
    } catch (error) {
      setUserData(null)
    }
  }

  const validateForm = (data, requiredsForm) => {
    let isValid = true

    if ([KYC_CARD_TYPE.THAI_ID, KYC_CARD_TYPE.WORK_PERMIT].includes(data?.cardType) && data?.cardId?.length < 13) {
      return false
    }

    Object.entries(data).forEach(([key, item]) => {
      if (!requiredsForm.includes(key)) {
        return
      }

      if (key === 'expiredAt' && data.neverExpire) return

      if (typeof item === 'object' && !Array.isArray(item)) {
        if (Object.values(item).some((value) => !value)) {
          isValid = false
        }
      } else if (typeof item === 'string' && !item) {
        isValid = false
      }
    })

    return isValid
  }

  const handleSubmit = () => {
    if (decisionIdCard && decisionBank) {
      let isError = false

      if (decisionIdCard === 'REJECTED' && reasonIdCard.length === 0) {
        setInvalid((prevInvalid) => ({ ...prevInvalid, reasonIdCard: true }))
        isError = true
      }

      if (decisionBank === 'REJECTED' && reasonBank.length === 0) {
        setInvalid((prevInvalid) => ({ ...prevInvalid, reasonBank: true }))
        isError = true
      }

      if (isError) {
        Swal.fire({
          title: 'กรุณาเลือกเหตุผลในกรณีที่\nบัตรประชาชน หรือบัญชีธนาคาร\nไม่ถูกต้อง',
          icon: 'error',
          showCancelButton: false,
          confirmButtonColor: Colors.LIGHT_RED,
          confirmButtonText: 'ปิด',
        })
        return
      }

      if (
        decisionIdCard === 'APPROVED' &&
        validateForm(userKYCData.cardInfo, requiredThai) === false &&
        userKYCData.cardInfo.cardType === KYC_CARD_TYPE.THAI_ID
      ) {
        Swal.fire({
          title: 'กรุณากรอกข้อมูลบัตรประชาชนให้ครบถ้วน!',
          icon: 'error',
          showCancelButton: false,
          confirmButtonColor: Colors.LIGHT_RED,
          confirmButtonText: 'ปิด',
        })
        return
      }

      if (decisionIdCard === 'APPROVED' && validateForm(userKYCData.cardInfo, requiredForeign) === false) {
        Swal.fire({
          title: 'กรุณากรอกข้อมูลชาวต่างชาติให้ครบถ้วน!',
          icon: 'error',
          showCancelButton: false,
          confirmButtonColor: Colors.LIGHT_RED,
          confirmButtonText: 'ปิด',
        })
        return
      }

      if (decisionBank === 'APPROVED' && validateForm(userKYCData.bookBankInfo, requiredBank) === false) {
        Swal.fire({
          title: 'กรุณากรอกข้อมูลหน้าสมุดบัญชีให้ครบถ้วน!',
          icon: 'error',
          showCancelButton: false,
          confirmButtonColor: Colors.LIGHT_RED,
          confirmButtonText: 'ปิด',
        })
        return
      }

      displaySwalConfirm()
    } else {
      Swal.fire({
        title: 'กรุณากรอกข้อมูลให้ครบถ้วน!',
        icon: 'error',
        showCancelButton: false,
        confirmButtonColor: Colors.LIGHT_RED,
        confirmButtonText: 'ปิด',
      })
    }
  }

  const verifyUserKYC = async (kycId) => {
    const cardInfo = userKYCData.cardInfo
    const isThaiId = cardInfo.cardType === KYC_CARD_TYPE.THAI_ID

    try {
      const cardIdInfo =
        decisionIdCard === 'REJECTED'
          ? {
              status: decisionIdCard,
              reasons: reasonIdCard,
            }
          : {
              cardId: cardInfo.cardId,
              firstName: cardInfo.firstName,
              lastName: cardInfo.lastName,
              cardType: cardInfo.cardType,
              address: cardInfo.address,
              foreignerStatus: cardInfo.foreignerStatus,
              customForeignerStatus: cardInfo.customForeignerStatus,
              status: decisionIdCard,
              reasons: reasonIdCard,
              birthDate: new Date(
                isThaiId ? cardInfo.birthDate.year - 543 : cardInfo.birthDate.year,
                cardInfo.birthDate.month - 1,
                cardInfo.birthDate.day,
              ).toISOString(),
              issueAt: isThaiId
                ? new Date(cardInfo.issueAt.year - 543, cardInfo.issueAt.month - 1, cardInfo.issueAt.day).toISOString()
                : null,
              expiredAt:
                cardInfo.neverExpire || !isThaiId
                  ? null
                  : new Date(
                      cardInfo.expiredAt.year - 543,
                      cardInfo.expiredAt.month - 1,
                      cardInfo.expiredAt.day,
                    ).toISOString(),
            }

      const bookBankInfo =
        decisionBank === 'REJECTED'
          ? { status: decisionBank, reasons: reasonBank }
          : {
              accountNo: userKYCData.bookBankInfo.accountNo,
              bankCode: bankCode[userKYCData.bookBankInfo.bankCode],
              status: decisionBank,
              reasons: reasonBank,
            }

      const body = {
        cardIdInfo: cardIdInfo,
        bookBankInfo: bookBankInfo,
      }
      const response = await fetch(`${config.apiAdminGateway}/api/v1/admin/kyc/${kycId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      }).then((res) => res.json())

      if (response.status.code !== 0) throw new Error(response.status.code)

      Swal.fire({
        title: 'ทำรายการสำเร็จ',
        icon: 'success',
        showCancelButton: false,
        confirmButtonColor: Colors.LIGHT_RED,
        confirmButtonText: 'ปิด',
      }).then(() => {
        onClose()
      })
    } catch (error) {
      console.error(error.message)
      let text = 'กรุณาทำรายการใหม่อีกครั้ง'
      switch (error.message) {
        case '120201':
          text =
            cardInfo.cardType === KYC_CARD_TYPE.THAI_ID
              ? 'เลขบัตรประจำตัวประชาชนไม่ถูกต้อง'
              : cardInfo.cardType === KYC_CARD_TYPE.PASSPORT
              ? 'เลข Passport ไม่ถูกต้อง'
              : 'เลข Work Permit ไม่ถูกต้อง'
          break
        case '120210':
          text = 'ข้อมูล ชื่อ-นามสกุล ไม่ตรงกับข้อมูลลูกค้าที่เคยลงทะเบียนไว้'
          break
        case '120211':
          text = 'ข้อมูล เลขบัญชีธนาคาร ไม่ตรงกับข้อมูลลูกค้าที่เคยลงทะเบียนไว้'
          break
        case '120212':
          text = 'ข้อมูล วันเกิด ไม่ตรงกับข้อมูลลูกค้าที่เคยลงทะเบียนไว้'
          break
        case '120214':
          text = 'ข้อมูล ธนาคาร ไม่ตรงกับข้อมูลลูกค้าที่เคยลงทะเบียนไว้'
          break
        case '1002':
          text = 'กรุณาเลือกจุดประสงค์'
          break
        default:
          break
      }
      Swal.fire({
        title: 'เกิดข้อผิดพลาด',
        text: text,
        icon: 'error',
        showCancelButton: false,
        confirmButtonColor: Colors.LIGHT_RED,
        confirmButtonText: 'ปิด',
      })
    }
  }

  const displaySwalConfirm = () => {
    const content = `
      <div style="text-align: start;">
        <h4>ลูกค้ารหัส: ${userKYCData.user.userNo}</h4>
        <div class='p-1'>
          <h4 style='justify-content: center;'>
            <i class='${
              decisionIdCard === KYC_STATUSES.APPROVED ? 'fa fa-check-circle-o' : 'fa fa-exclamation-circle'
            }' 
              style='color: ${
                decisionIdCard === KYC_STATUSES.APPROVED ? 'green' : 'red'
              }; font-size: 25px; margin-right: 8px;'>
            </i>
            ข้อมูลบัตรประชาชน
          </h4>
          <ul>
            ${reasonIdCard.map((reason) => `<li> - ${reason}</li>`).join('')}
          </ul>
        </div>
        <div class='p-1'>
          <h4 style='justify-content: center;'>
            <i class='${decisionBank === KYC_STATUSES.APPROVED ? 'fa fa-check-circle-o' : 'fa fa-exclamation-circle'}' 
              style='color: ${
                decisionBank === KYC_STATUSES.APPROVED ? 'green' : 'red'
              }; font-size: 25px; margin-right: 8px;'>
            </i>
            ข้อมูลบัญชีธนาคาร
          </h4>
          <ul>
            ${reasonBank.map((reason) => `<li> - ${reason}</li>`).join('')}
          </ul>
        </div>
      </div>
    `
    Swal.fire({
      title: 'ยืนยันข้อมูล',
      html: content,
      showCancelButton: true,
      showConfirmButton: true,
      confirmButtonColor: Colors.GREEN,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(async (result) => {
      if (result.isConfirmed) {
        verifyUserKYC(selectedKYCUser.id)
      }
    })
  }

  useEffect(() => {
    if (selectedKYCUser.id) {
      fetchKYC(selectedKYCUser.id)
    }
  }, [fetchKYC, selectedKYCUser.id])

  if (!kycUserModalVisible || !userKYCData?.user) return null

  return (
    <Modal
      className="fade"
      show={kycUserModalVisible}
      size="xxl"
      scrollable
      centered
      enforceFocus={false}
      onHide={onClose}
    >
      <Modal.Header closeButton>
        <Row style={{ width: '100%' }}>
          <Col xs={10}>
            <Modal.Title>รหัสลูกค้า: {userKYCData?.user?.userNo}</Modal.Title>
          </Col>
          <Col xs={2} className="d-flex justify-content-end p-8">
            <Badge variant={KYC_STATUSES_COLOR_LIGHT[userKYCData.status]}>{userKYCData.status}</Badge>
          </Col>
        </Row>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" role="status" style={{ width: '64px', height: '64px' }}>
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <SRLWrapper>
            <Row>
              <Col xs={12}>
                <UserDetailCard userData={userData} />
              </Col>
              <Col xs={12} sm={12} lg={6}>
                <KYCIdCardForm
                  userKYCDataRef={userKYCDataRef}
                  cardInfo={userKYCData.cardInfo}
                  setUserKYCData={setUserKYCData}
                  decision={decisionIdCard}
                  setDecision={setDecisionIdCard}
                  invalid={invalid}
                  setInvalid={setInvalid}
                  reasons={reasonIdCard}
                  setReasons={setReasonIdCard}
                />
              </Col>
              <Col xs={12} sm={12} lg={6}>
                <KYCBankForm
                  bookBankInfo={userKYCData.bookBankInfo}
                  setUserKYCData={setUserKYCData}
                  decision={decisionBank}
                  setDecision={setDecisionBank}
                  invalid={invalid}
                  setInvalid={setInvalid}
                  reasons={reasonBank}
                  setReasons={setReasonBank}
                />
              </Col>
            </Row>
          </SRLWrapper>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn" onClick={onClose}>
          ยกเลิก
        </button>
        {userKYCData?.user?.userNo && (
          <Button
            variant="success"
            onClick={handleSubmit}
            color={Colors.GREEN}
            disabled={!decisionBank || !decisionIdCard}
          >
            ยืนยัน
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

KYCUserModal.propTypes = {
  kycUserModalVisible: PropsTypes.bool.isRequired,
  selectedKYCUser: PropsTypes.object.isRequired,
  setSelectedKYCUser: PropsTypes.func.isRequired,
  onClose: PropsTypes.func.isRequired,
}

export default KYCUserModal
